// src/features/fourlink-config/components/blocks/Block1Steps/ActivosStep.jsx

import React, { useMemo, useState } from 'react';
import { CommentButton } from '../../shared/CommentButton';

export const ActivosStep = ({
  activosPorCategoriaDisponibles,
  hiddenCategoriasActivos,
  hiddenActivosPorCategoria,
  selectedActivos,
  selectedPlanes,
  activosConfig,
  collapsedCategoriasActivos,
  activosErrors,
  onToggleActivo,
  onUpdateActivoConfig,
  onAddCategoriaActivo,
  onAddActivoEnCategoria,
  onHideCategorias,
  onRestoreCategoria,
  onHideActivos,
  onRestoreActivo,
  onToggleCollapse,
  onCommentClick,
  hasComment,
  getCategoriaComment,
  getActivoComment,
}) => {
  const [isAddCategoriaModalOpen, setIsAddCategoriaModalOpen] = useState(false);
  const [isHideCategoriasModalOpen, setIsHideCategoriasModalOpen] = useState(false);
  const [newCategoriaName, setNewCategoriaName] = useState('');
  const [categoriasToHide, setCategoriasToHide] = useState([]);

  const [categoriaModalData, setCategoriaModalData] = useState(null); // {categoria, mode:'add'|'hide'}
  const [newActivoName, setNewActivoName] = useState('');
  const [activosToHide, setActivosToHide] = useState([]);

  const categoriasEntries = useMemo(() => Object.entries(activosPorCategoriaDisponibles || {}), [
    activosPorCategoriaDisponibles,
  ]);

  const hiddenCategoriasList = useMemo(
    () => (Array.isArray(hiddenCategoriasActivos) ? hiddenCategoriasActivos : []),
    [hiddenCategoriasActivos],
  );

  const toggleCategoriaToHide = (categoria) => {
    setCategoriasToHide((prev) =>
      prev.includes(categoria) ? prev.filter((c) => c !== categoria) : [...prev, categoria],
    );
  };

  const handleConfirmHideCategorias = () => {
    if (typeof onHideCategorias === 'function' && categoriasToHide.length > 0) {
      onHideCategorias(categoriasToHide, activosPorCategoriaDisponibles);
    }
    setCategoriasToHide([]);
    setIsHideCategoriasModalOpen(false);
  };

  const handleCreateCategoria = (e) => {
    e.preventDefault();
    if (!newCategoriaName.trim()) return;
    const created = typeof onAddCategoriaActivo === 'function' ? onAddCategoriaActivo(newCategoriaName.trim()) : false;
    if (created === false) return;
    setNewCategoriaName('');
    setIsAddCategoriaModalOpen(false);
  };

  const openActivosModal = (categoria, mode) => {
    setCategoriaModalData({ categoria, mode });
    setNewActivoName('');
    setActivosToHide([]);
  };

  const handleCreateActivo = (e) => {
    e.preventDefault();
    if (!categoriaModalData?.categoria || !newActivoName.trim()) return;
    const created = typeof onAddActivoEnCategoria === 'function'
      ? onAddActivoEnCategoria(categoriaModalData.categoria, newActivoName.trim())
      : false;
    if (created === false) return;
    setNewActivoName('');
    setCategoriaModalData(null);
  };

  const handleHideActivos = () => {
    if (!categoriaModalData?.categoria || activosToHide.length === 0) return;
    if (typeof onHideActivos === 'function') {
      onHideActivos(categoriaModalData.categoria, activosToHide);
    }
    setActivosToHide([]);
    setCategoriaModalData(null);
  };

  const toggleActivoToHide = (name) => {
    setActivosToHide((prev) =>
      prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name],
    );
  };

  const renderModalShell = (children) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-3xl font-bold" style={{ color: '#1a365d' }}>Activos</h2>
        <div className="flex items-center gap-3">
          {hasComment && (
            <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
          )}
          <button
            type="button"
            onClick={() => setIsHideCategoriasModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors font-semibold"
            title="Ocultar categoria de activos"
          >
            <span className="sr-only">Ocultar categoria de activos</span>
            <span className="text-lg leading-none font-bold">-</span>
          </button>
          <button
            type="button"
            onClick={() => setIsAddCategoriaModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
            title="Anadir categoria de activo"
          >
            <span className="sr-only">Anadir categoria de activo</span>
            <span className="text-xl leading-none font-bold">+</span>
          </button>
          <CommentButton
            onClick={() => onCommentClick({ key: 'section:activos', label: 'Activos' })}
            hasComment={hasComment}
            srLabel="Anadir comentario para Activos"
            variant="circle"
            className="border-blue-200 text-blue-700 bg-white hover:bg-blue-50"
          />
        </div>
      </div>
      <p className="text-gray-600 mb-4 text-lg">Selecciona los activos y configura sus parametros:</p>

      {activosErrors.length > 0 && (
        <div className="p-4 border border-red-200 bg-red-50 text-red-800 rounded-lg">
          <div className="font-semibold mb-1">Errores del backend detectados</div>
          <ul className="list-disc pl-5 text-sm">
            {activosErrors.map((msg, i) => (
              <li key={i} className="break-words">{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {categoriasEntries.length === 0 ? (
          <div className="p-4 border rounded-lg bg-gray-50 text-gray-600">
            No hay activos disponibles.
          </div>
        ) : (
          categoriasEntries.map(([categoria, listaActivos]) => {
            const categoriaKey = `categoria-activo:${categoria}`;
            const categoriaHasComment = getCategoriaComment(categoria);
            const isCategoriaCollapsed = collapsedCategoriasActivos[categoria] ?? true;
            const hiddenList = hiddenActivosPorCategoria?.[categoria] || [];
            const selectedCount = (listaActivos || []).filter((a) => selectedActivos.includes(a)).length;

            return (
              <div key={categoria} className="border rounded-lg">
                <div className="flex items-center justify-between gap-3 p-3 bg-gray-100 rounded-t-lg">
                  <button
                    type="button"
                    onClick={() => onToggleCollapse(categoria)}
                    className="flex flex-1 items-center justify-between text-left px-3 py-2 border border-blue-300 rounded-full text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    <span className="font-semibold text-lg text-blue-700">{categoria}</span>
                    <span className="text-sm text-blue-700">
                      {isCategoriaCollapsed ? 'v' : '^'}
                    </span>
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openActivosModal(categoria, 'hide')}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors"
                      title={`Ocultar activos de ${categoria}`}
                    >
                      <span className="sr-only">Ocultar activos</span>
                      <span className="text-lg leading-none font-bold">-</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openActivosModal(categoria, 'add')}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
                      title={`Anadir activo en ${categoria}`}
                    >
                      <span className="sr-only">Anadir activo</span>
                      <span className="text-lg leading-none font-bold">+</span>
                    </button>
                    <CommentButton
                      onClick={() => onCommentClick({ key: categoriaKey, label: `Categoria de activo ${categoria}` })}
                      hasComment={categoriaHasComment}
                      srLabel={`${categoriaHasComment ? 'Editar' : 'Anadir'} comentario para la categoria de activo ${categoria}`}
                      variant="circle"
                    />
                    {categoriaHasComment && (
                      <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
                    )}
                  </div>
                </div>

                {!isCategoriaCollapsed && (
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-full text-sm text-gray-600">
                      {selectedCount}/{(listaActivos || []).length} seleccionados
                    </div>
                    {(listaActivos || []).length === 0 ? (
                      <div className="col-span-full text-sm text-gray-500 italic">
                        No hay activos disponibles en esta categoria.
                      </div>
                    ) : (
                      (listaActivos || []).map((activo) => {
                        const activoKey = `activo:${categoria}::${activo}`;
                        const activoHasComment = getActivoComment(activoKey);

                        return (
                          <div key={`${categoria}-${activo}`} className="border border-gray-200 rounded-xl p-6 bg-white hover:shadow-md transition-all">
                            <div className="flex items-start justify-between gap-4 mb-4">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={selectedActivos.includes(activo)}
                                  onChange={() => onToggleActivo(activo)}
                                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label className="font-semibold text-lg" style={{ color: '#1a365d' }}>{activo}</label>
                              </div>
                              <div className="flex items-center gap-2">
                                <CommentButton
                                  onClick={() => onCommentClick({ key: activoKey, label: `Activo ${activo}` })}
                                  hasComment={activoHasComment}
                                  srLabel={`${activoHasComment ? 'Editar' : 'Anadir'} comentario para el activo ${activo}`}
                                  variant="circle"
                                />
                                {activoHasComment && (
                                  <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
                                )}
                              </div>
                            </div>

                            {selectedActivos.includes(activo) && (
                              <div className="space-y-4 pl-2">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Planes asociados:</label>
                                  <select
                                    multiple
                                    size="4"
                                    value={activosConfig[activo]?.planes || []}
                                    onChange={(e) => onUpdateActivoConfig(activo, 'planes', Array.from(e.target.selectedOptions, option => option.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  >
                                    {selectedPlanes.map((plan) => (
                                      <option key={plan} value={plan} className="py-1">{plan}</option>
                                    ))}
                                  </select>
                                  <p className="text-xs text-gray-500 mt-1">Mantener Ctrl/Cmd para seleccionar multiples planes</p>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <span className="block text-sm font-medium text-gray-700 mb-2">Unidad de medida:</span>
                                    <input
                                      type="text"
                                      placeholder="ej: C, min, kg"
                                      value={activosConfig[activo]?.unidad || ''}
                                      onChange={(e) => onUpdateActivoConfig(activo, 'unidad', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <span className="block text-sm font-medium text-gray-700 mb-2">Valor minimo:</span>
                                    <input
                                      type="number"
                                      step="1"
                                      placeholder="0"
                                      value={activosConfig[activo]?.min || ''}
                                      onChange={(e) => onUpdateActivoConfig(activo, 'min', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <span className="block text-sm font-medium text-gray-700 mb-2">Valor maximo:</span>
                                    <input
                                      type="number"
                                      step="1"
                                      placeholder="100"
                                      value={activosConfig[activo]?.max || ''}
                                      onChange={(e) => onUpdateActivoConfig(activo, 'max', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                </div>

                                {(activosConfig[activo]?.unidad || activosConfig[activo]?.min || activosConfig[activo]?.max) && (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <p className="text-sm font-medium text-blue-800 mb-1">Configuracion actual:</p>
                                    <p className="text-sm text-blue-700">
                                      Rango: {activosConfig[activo]?.min || '___'} - {activosConfig[activo]?.max || '___'} {activosConfig[activo]?.unidad || ''}
                                    </p>
                                    {activosConfig[activo]?.planes && activosConfig[activo].planes.length > 0 && (
                                      <p className="text-sm text-blue-700 mt-1">
                                        Planes: {activosConfig[activo].planes.join(', ')}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {hiddenList.length > 0 && (
                      <div className="col-span-full p-3 border-t text-sm text-gray-600">
                        <strong>Activos ocultos en la categoria:</strong> {hiddenList.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {isAddCategoriaModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Anadir categoria</h3>
              <p className="text-sm text-gray-600">Crea nuevas categorias y revisa las que has ocultado.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={() => { setIsAddCategoriaModalOpen(false); setNewCategoriaName(''); }}
              aria-label="Cerrar modal de anadir categoria"
            >
              x
            </button>
          </div>

          <form onSubmit={handleCreateCategoria} className="space-y-3">
            <label className="block text-sm font-medium text-gray-700" htmlFor="nueva-categoria-activo-input">
              Nombre de la categoria
            </label>
            <input
              id="nueva-categoria-activo-input"
              type="text"
              value={newCategoriaName}
              onChange={(e) => setNewCategoriaName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ej. Refrigeracion"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setIsAddCategoriaModalOpen(false); setNewCategoriaName(''); }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                disabled={!newCategoriaName.trim()}
              >
                Anadir categoria
              </button>
            </div>
          </form>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Categorias ocultas</h4>
            {hiddenCategoriasList.length === 0 ? (
              <p className="text-sm text-gray-500">No hay categorias ocultas.</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {hiddenCategoriasList.map((item) => (
                  <li key={item} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-800">{item}</span>
                    {typeof onRestoreCategoria === 'function' && (
                      <button
                        type="button"
                        onClick={() => onRestoreCategoria(item)}
                        className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        Restaurar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isHideCategoriasModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Ocultar categorias</h3>
              <p className="text-sm text-gray-600">Selecciona las categorias que quieras ocultar temporalmente.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={() => { setIsHideCategoriasModalOpen(false); setCategoriasToHide([]); }}
              aria-label="Cerrar modal de ocultar categorias"
            >
              x
            </button>
          </div>

          {categoriasEntries.length === 0 ? (
            <p className="text-sm text-gray-500">No hay categorias disponibles para ocultar.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {categoriasEntries.map(([cat]) => (
                <label
                  key={cat}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-200"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={categoriasToHide.includes(cat)}
                      onChange={() => toggleCategoriaToHide(cat)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-800">{cat}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setIsHideCategoriasModalOpen(false); setCategoriasToHide([]); }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmHideCategorias}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              disabled={categoriasToHide.length === 0}
            >
              Ocultar seleccionadas
            </button>
          </div>
        </div>
      )}

      {categoriaModalData && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {categoriaModalData.mode === 'add' ? 'Anadir activo' : 'Ocultar activos'}
              </h3>
              <p className="text-sm text-gray-600">Categoria: {categoriaModalData.categoria}</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={() => { setCategoriaModalData(null); setNewActivoName(''); setActivosToHide([]); }}
              aria-label="Cerrar modal de activos"
            >
              x
            </button>
          </div>

          {categoriaModalData.mode === 'add' && (
            <>
              <form onSubmit={handleCreateActivo} className="space-y-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="nuevo-activo-input">
                  Nombre del activo
                </label>
                <input
                  id="nuevo-activo-input"
                  type="text"
                  value={newActivoName}
                  onChange={(e) => setNewActivoName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej. Horno mixto"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setCategoriaModalData(null); setNewActivoName(''); }}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                    disabled={!newActivoName.trim()}
                  >
                    Anadir activo
                  </button>
                </div>
              </form>

              <div className="border-t pt-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Activos ocultos en esta categoria</h4>
                {hiddenActivosPorCategoria?.[categoriaModalData.categoria]?.length ? (
                  <ul className="space-y-2 max-h-32 overflow-y-auto pr-1">
                    {hiddenActivosPorCategoria[categoriaModalData.categoria].map((item) => (
                      <li key={item} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-sm text-gray-800">{item}</span>
                        {typeof onRestoreActivo === 'function' && (
                          <button
                            type="button"
                            onClick={() => onRestoreActivo(categoriaModalData.categoria, item)}
                            className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            Restaurar
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No hay activos ocultos en este grupo.</p>
                )}
              </div>
            </>
          )}

          {categoriaModalData.mode === 'hide' && (
            <>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {(activosPorCategoriaDisponibles[categoriaModalData.categoria] || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No hay activos disponibles para ocultar.</p>
                ) : (
                  activosPorCategoriaDisponibles[categoriaModalData.categoria].map((a) => (
                    <label
                      key={a}
                      className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-200"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={activosToHide.includes(a)}
                          onChange={() => toggleActivoToHide(a)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-800">{a}</span>
                      </div>
                      {selectedActivos.includes(a) && (
                        <span className="text-xs text-blue-600 font-medium">Seleccionado</span>
                      )}
                    </label>
                  ))
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setCategoriaModalData(null); setActivosToHide([]); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleHideActivos}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  disabled={activosToHide.length === 0}
                >
                  Ocultar seleccionados
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
